import React, { useCallback, useEffect, useState } from "react";
import { api, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FormSkeleton } from "@/components/ui/page-skeletons";
import { PageHeader } from "@/components/patterns";
import { ErrorState } from "@/components/patterns/States";
import { Cloud, DatabaseBackup, Loader2, RefreshCw, Trash2, AlertTriangle, CheckCircle2 } from "lucide-react";
import { dateLocale, withClock } from "@/lib/dateTime";

type Area = { key: string; label: string; note: string; files: number; bytes: number; orphan_files: number; orphan_bytes: number };
type Backups =
  | { configured: false }
  | { configured: true; bucket: string; error: string }
  | { configured: true; bucket: string; count: number; latest: { name: string; size: number; at: string }[] };
type Overview = {
  status: { bucket: string; reachable: boolean; error?: string | null; endpoint?: string; access_key_hint?: string };
  areas: Area[];
  totals: { files: number; bytes: number; orphan_files: number; orphan_bytes: number };
  orphan_grace_hours: number;
  backups: Backups;
  counted_at: string;
};

type TestStep = { step: string; ok: boolean; code?: string; status?: number; message?: string };
type TestResult = { bucket: string; endpoint: string; access_key_hint: string; ok: boolean; steps: TestStep[] };

/** What the pattern of answers means, in plain words */
function explainTest(t: TestResult): string {
  const failed = t.steps.filter((x) => !x.ok);
  if (!failed.length) return "Everything the CRM needs works: reach, list, save, read and delete.";
  const codes = failed.map((x) => x.code || "").join(" ");
  if (failed.length === t.steps.length) {
    if (/NoSuchBucket/i.test(codes)) return `The storage folder "${t.bucket}" wasn't found. Check the folder name in the file storage settings. A folder created in the EU needs the EU storage address.`;
    if (/InvalidAccessKeyId|SignatureDoesNotMatch/i.test(codes)) return "File storage doesn't accept this key pair. Use the key ID and secret key shown once when the key was created, with no spaces.";
    return `Every step was refused. File storage doesn't accept this key for the folder "${t.bucket}": the key isn't allowed to use this folder, the account ID is for another account, or the key only works from certain places. If you just changed the settings, restart the CRM so it picks them up.`;
  }
  const bad = failed.map((x) => x.step.toLowerCase()).join(", ");
  return `Some steps work and some don't (${bad}). The key's permission is too narrow: it needs read and write access to this folder.`;
}

const fmtBytes = (n: number) => {
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let v = n / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v >= 100 ? v.toFixed(0) : v.toFixed(1)} ${units[i]}`;
};
const fmtWhen = (iso: string) => new Date(iso).toLocaleString(dateLocale(), withClock({ day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }));
const plural = (n: number, word: string) => `${n.toLocaleString("en-IN")} ${word}${n === 1 ? "" : "s"}`;

export function StoragePage() {
  const { success, error } = useToast();
  const [data, setData] = useState<Overview | null>(null);
  const [problem, setProblem] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState<{ area?: string; label: string; files: number; bytes: number } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [test, setTest] = useState<TestResult | null>(null);

  const runTest = async () => {
    setTesting(true);
    try {
      setTest(unwrapObject<TestResult>(await api.post("/api/Storage/Test")));
    } catch (err: any) {
      error("Could not run the test", err.message || "Please try again");
    } finally {
      setTesting(false);
    }
  };

  const load = useCallback(async (refresh = false) => {
    setBusy(true);
    try {
      setData(unwrapObject<Overview>(await api.get(`/api/Storage/Overview${refresh ? "?refresh=1" : ""}`)));
      setProblem(null);
    } catch (err) {
      setProblem(err);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const deleteOrphans = async () => {
    if (!confirm) return;
    setDeleting(true);
    try {
      const r = unwrapObject<{ removed: number; bytes: number }>(await api.post("/api/Storage/Orphans/Delete", confirm.area ? { area: confirm.area } : {}));
      success("Orphaned files removed", `${plural(r.removed, "file")} (${fmtBytes(r.bytes)}) deleted`);
      setConfirm(null);
      await load(true);
    } catch (err: any) {
      error("Could not remove the files", err.message || "Please try again");
    } finally {
      setDeleting(false);
    }
  };

  if (!data && !problem) return <FormSkeleton />;
  if (problem || !data) return <ErrorState title="Couldn't load storage" body="Try again in a moment." onRetry={() => load(true)} error={problem} />;

  const { status, areas, totals, backups } = data;

  return (
    <div className="space-y-6 max-w-5xl mx-auto">
      <PageHeader
        title="Storage"
        summary={`${plural(totals.files, "file")} · ${fmtBytes(totals.bytes)}`}
        actions={
          <>
            <Button variant="outline" size="sm" onClick={runTest} disabled={testing}>
              {testing ? <Loader2 className="size-4 animate-spin" /> : <CheckCircle2 className="size-4" />} Test connection
            </Button>
            <Button variant="outline" size="sm" onClick={() => load(true)} disabled={busy}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : <RefreshCw className="size-4" />} Recount
            </Button>
          </>
        }
      />

      {/* Where files are kept */}
      <Card className="shadow-xs">
        <CardHeader>
          <div className="flex items-center gap-2.5">
            <div className="size-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><Cloud className="size-4" /></div>
            <div>
              <CardTitle className="text-base font-semibold">Where files are kept</CardTitle>
              <CardDescription>Uploaded files are private. They open only from the record they belong to, after the usual access check.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="grid gap-3 sm:grid-cols-3 text-sm">
          <div>
            <div className="text-xs text-muted-foreground">Storage</div>
            <div className="font-semibold">Private file storage</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Storage folder</div>
            <div className="font-semibold font-mono text-[13px]">{status.bucket}</div>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">Connection</div>
            {status.reachable ? (
              <div className="font-semibold text-success inline-flex items-center gap-1.5"><CheckCircle2 className="size-4" /> Working</div>
            ) : (
              <div className="font-semibold text-destructive inline-flex items-center gap-1.5"><AlertTriangle className="size-4" /> Can't be reached</div>
            )}
          </div>
          {status.endpoint && (
            <p className="sm:col-span-3 text-xs text-muted-foreground font-mono">
              Key {status.access_key_hint}
            </p>
          )}
          {test && (
            <div className="sm:col-span-3 rounded-lg border border-border bg-muted/40 p-3 space-y-2" role="status">
              <ul className="space-y-1">
                {test.steps.map((x) => (
                  <li key={x.step} className="flex items-start gap-2 text-[13px]">
                    {x.ok ? <CheckCircle2 className="size-4 text-success shrink-0 mt-0.5" /> : <AlertTriangle className="size-4 text-destructive shrink-0 mt-0.5" />}
                    <span>
                      <span className="font-medium">{x.step}</span>
                      {!x.ok && <span className="text-muted-foreground"> — didn't work</span>}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-foreground">{explainTest(test)}</p>
            </div>
          )}
          {!status.reachable && (
            <p className="sm:col-span-3 text-xs text-destructive">{data.status.error ? `${data.status.error} New uploads and downloads fail until this is fixed.` : "New uploads and downloads are failing. Check the file storage settings and the key's permissions."}</p>
          )}
        </CardContent>
      </Card>

      {/* Usage */}
      <Card className="shadow-xs">
        <CardHeader>
          <CardTitle className="text-base font-semibold">What is stored</CardTitle>
          <CardDescription>Counted {fmtWhen(data.counted_at)}. This page shows totals only; files themselves open from their deal, customer, chat or email.</CardDescription>
        </CardHeader>
        <CardContent className="px-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="pl-6">Kind</TableHead>
                <TableHead className="text-right">Files</TableHead>
                <TableHead className="text-right">Size</TableHead>
                <TableHead className="text-right pr-6">Left behind</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {areas.map((a) => (
                <TableRow key={a.key}>
                  <TableCell className="pl-6">
                    <div className="font-medium">{a.label}</div>
                    <div className="text-xs text-muted-foreground">{a.note}</div>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{a.files.toLocaleString("en-IN")}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtBytes(a.bytes)}</TableCell>
                  <TableCell className="text-right pr-6 tabular-nums">
                    {a.orphan_files > 0 ? (
                      <button type="button" className="text-warning hover:underline" onClick={() => setConfirm({ area: a.key, label: a.label, files: a.orphan_files, bytes: a.orphan_bytes })}>
                        {a.orphan_files.toLocaleString("en-IN")} · {fmtBytes(a.orphan_bytes)}
                      </button>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow className="font-semibold">
                <TableCell className="pl-6">Total</TableCell>
                <TableCell className="text-right tabular-nums">{totals.files.toLocaleString("en-IN")}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtBytes(totals.bytes)}</TableCell>
                <TableCell className="text-right pr-6 tabular-nums">{totals.orphan_files ? `${totals.orphan_files.toLocaleString("en-IN")} · ${fmtBytes(totals.orphan_bytes)}` : "—"}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Clean-up */}
      <Card className="shadow-xs">
        <CardHeader>
          <CardTitle className="text-base font-semibold">Clean-up</CardTitle>
          <CardDescription>
            Files left behind after their deal, customer, chat message or email was deleted (or a logo that was replaced). Nothing can open them any more. Files newer than {data.orphan_grace_hours} hours are never counted.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3">
          {totals.orphan_files > 0 ? (
            <>
              <div className="text-sm">
                <span className="font-semibold">{plural(totals.orphan_files, "file")}</span> ({fmtBytes(totals.orphan_bytes)}) can be removed.
              </div>
              <Button variant="destructive" size="sm" onClick={() => setConfirm({ label: "all kinds of file", files: totals.orphan_files, bytes: totals.orphan_bytes })}>
                <Trash2 className="size-4" /> Delete left-behind files
              </Button>
            </>
          ) : (
            <div className="text-sm text-muted-foreground inline-flex items-center gap-1.5"><CheckCircle2 className="size-4 text-success" /> Nothing is left behind.</div>
          )}
        </CardContent>
      </Card>

      {/* Backups */}
      <Card className="shadow-xs">
        <CardHeader>
          <div className="flex items-center gap-2.5">
            <div className="size-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center"><DatabaseBackup className="size-4" /></div>
            <div>
              <CardTitle className="text-base font-semibold">Off-site database backups</CardTitle>
              <CardDescription>Each release saves a copy of the database before it changes anything. A copy kept elsewhere is safe even if the main copy is lost.</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {!backups.configured ? (
            <p className="text-sm text-muted-foreground">
              Not set up. Add a backup storage folder and its key to the CRM's file storage settings, and every release will copy its backup there.
            </p>
          ) : "error" in backups ? (
            <p className="text-sm text-destructive inline-flex items-center gap-1.5"><AlertTriangle className="size-4" /> {backups.error}</p>
          ) : backups.latest.length === 0 ? (
            <p className="text-sm text-muted-foreground">The backup folder is connected, but no backup has been copied yet. The next release will add one.</p>
          ) : (
            <div className="space-y-2">
              <div className="text-sm">
                <Badge variant="secondary">{plural(backups.count, "backup")}</Badge>
                <span className="ml-2 text-muted-foreground">Latest: {fmtWhen(backups.latest[0]!.at)}</span>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Backup</TableHead>
                    <TableHead>Saved</TableHead>
                    <TableHead className="text-right">Size</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {backups.latest.map((b) => (
                    <TableRow key={b.name}>
                      <TableCell className="font-mono text-[13px]">{b.name}</TableCell>
                      <TableCell>{fmtWhen(b.at)}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtBytes(b.size)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!confirm} onOpenChange={(o) => !o && !deleting && setConfirm(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete left-behind files?</DialogTitle>
            <DialogDescription>
              {confirm && `${plural(confirm.files, "file")} (${fmtBytes(confirm.bytes)}) in ${confirm.label} will be permanently deleted. They belong to records that no longer exist, so nobody can open them today. This is recorded in the security log and can't be undone.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(null)} disabled={deleting}>Cancel</Button>
            <Button variant="destructive" onClick={deleteOrphans} disabled={deleting}>
              {deleting ? <Loader2 className="size-4 animate-spin" /> : <Trash2 className="size-4" />} Delete files
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
